import i18next from 'i18next'
import React from 'react'
import { any, async, fromJSX, nothing, Updater, Widget } from 'widgets-for-react'
import { SearchableAgendaEventPage, SearchableNewsPage } from '../../../../backendApi/generated'
import { DecorativeIcon } from '../../../../utils/spaarneIcons'
import { Loader } from '../../../sharedLayout/loader'
import { GenericPageHeader, GenericPageHeaderWrapper } from '../../../sharedLayout/pageheader'
import { PageHeaderNavLayout } from '../../../sharedLayout/pageheaderUtils'
import { HighlightBlockLayout } from '../../blocks/block/highlightBlockLayout'
import { RelatedContentBlockLayout } from '../../blocks/block/relatedContentBlock/relatedContentBlockLayout'
import { blocksWidget } from '../../blocks/blocksWidget'
import { cmsPageStateUpdaters, DossierPage, Link, Page } from '../../cmsPageState'
import { DossierNewsResult } from '../../../../backendApi/generated'
import { transformImage } from '../../sharepointPageApi/transformers/pageDataToPage'
import { getDossierNews } from '../../sharepointPageApi/sharepointPageApi'
import { getImageUrls } from '../../../../utils/sharepointImageUrls'
import { highlightedNewsImageSrc, highlightedNewsImageSrcSet } from '../../../../utils/imageSizes'
import { DataLayer } from '../../../../utils/datalayer'
import { getCurrentView } from '../../../viewListener/viewListenerState'

const newsItemMapper = (n: SearchableNewsPage) => ({
  title: n.HeaderTitle ?? "No title",
  intro: n.TeaserDescription ?? "",
  url: n.AbsolutePath ?? "#",
  eventDate: undefined,
  image: transformImage({
    Title: n.HeaderImage?.Title ?? '',
    Url: n.HeaderImage?.Url ?? ''
  }),
  dossier: undefined
})

const agendaItemMapper = (a: SearchableAgendaEventPage) => ({
  title: a.HeaderTitle ?? "No title",
  intro: a.TeaserDescription ?? "",
  url: a.AbsolutePath ?? "#",
  eventDate: new Date(a.EventDate),
  image: transformImage({
    Title: a.HeaderImage?.Title ?? '',
    Url: a.HeaderImage?.Url ?? ''
  }),
  dossier: undefined
})

export const dossierPageWidget =
  (acceptVideoCookie: () => void) =>
    (props: DossierPage): Widget<Updater<Page>> =>
      any([
        async<DossierNewsResult>(props.dossierNews).map(cmsPageStateUpdaters.dossierNewsUpdater),
        async<SearchableAgendaEventPage[]>(props.agendaPages).map(cmsPageStateUpdaters.dossierAgendaUpdater),
        async<Link[]>(props.childrenState).map(cmsPageStateUpdaters.pageTreeStateUpdater),
        fromJSX((setState) => {
          const currentNewsPages = props.dossierNews.kind == "loaded" ? props.dossierNews.value.NewsPages ?? [] : []
          const allNewsPages = [...props.accumulatedNews, ...currentNewsPages]
          const highLightedNews = props.highlightedNews ??
            (props.dossierNews.kind == "loaded" ? props.dossierNews.value.HighlightedNewsPage : undefined)
          const totalNewsCount = props.totalNewsCount > 0
            ? props.totalNewsCount
            : (props.dossierNews.kind == "loaded" ? props.dossierNews.value.TotalCount : 0)
          const hasMoreNews = allNewsPages.length + (highLightedNews ? 1 : 0) < totalNewsCount
          const agendaPages = props.agendaPages.kind === "loaded" ? props.agendaPages.value : []

          // Spaarne pages that have this dossier as Hoofdpagina, preceded by the dossier itself
          const menuItems = props.childrenState.kind === "loaded"
            ? [
                { title: props.title, url: props.url, active: true },
                ...props.childrenState.value.map((child) => ({ ...child, active: false })),
              ]
            : []

          // The API returns 3 news items per page (see getDossierNews); "Load more" appends the next 3
          const newsBlock = allNewsPages.length > 0 &&
            <RelatedContentBlockLayout index={0}
              title={i18next.t("dossier:NewsSectionTitle")}
              items={allNewsPages.map(newsItemMapper)}
              readMoreNewsUrl={hasMoreNews ? i18next.t("dossier:AllNewsLabel") : undefined}
              pagination={hasMoreNews}
              onLoadMore={() => setState(cmsPageStateUpdaters.loadMoreDossierNewsUpdater(getDossierNews))}
            />

          const agendaBlock = agendaPages.length > 0 &&
            <RelatedContentBlockLayout index={3}
              title={i18next.t("dossier:AgendaSectionTitle")}
              items={agendaPages.map(agendaItemMapper)}
              pagination={false}
            />

          const highlightBlock = highLightedNews &&
            <HighlightBlockLayout
              index={1}
              title={highLightedNews.HeaderTitle ?? ""}
              intro={highLightedNews.TeaserDescription ?? highLightedNews.HeaderDescription ?? ""}
              primaryAction={{ title: i18next.t('common:readMore'), url: highLightedNews.AbsolutePath ?? "#" }}
              image={{
                alt: "[*] Highlighted news image",
                src: highlightedNewsImageSrc,
                srcset: highlightedNewsImageSrcSet,
                sizes: '100vw',
                lazyload: true,
                width: '400',
                height: '400',
                urls: getImageUrls(highLightedNews.HeaderImage?.Url ?? '')
              }}
              isLinkType={true}
            />

          return (
            <>
              <GenericPageHeaderWrapper kind="dossierPage">
                <GenericPageHeader
                  kind="dossierPage"
                  title={props.title}
                  intro={props.intro}
                  breadcrumbs={[
                    { title: 'Spaarne Gasthuis', url: '/' },
                    { title: props.title, url: props.url },
                  ]}
                  extraJSXBelowText={
                    <>
                      <div className="dossier__label label--m">
                        <DecorativeIcon iconId={"icon--folders"} width="24px" height="24px" />
                        <span>{i18next.t("dossier:DossierTag")}</span>
                      </div>
                      {menuItems.length > 1 && (
                        <PageHeaderNavLayout
                          kind="on-page-nav"
                          contactItems={[]}
                          items={menuItems}
                          heading={i18next.t("common:directlyTo")}
                          menuOpened={props.menuOpened}
                          toggleMenu={() => setState(cmsPageStateUpdaters.menuOpenedUpdater)}
                        />
                      )}
                    </>
                  }
                  quickUtils={{
                    shareLabel: i18next.t("dossier:ShareLabel"),
                    isDropdownOpen: props.quickUtilsState!.isDropdownOpen,
                    copyCompleted: props.quickUtilsState!.copyCompleted,
                    pageTitle: props.title,
                    onDropdownToggle: () => setState(cmsPageStateUpdaters.shareOptionsToggleUpdater),
                    onDropdownClose: () => setState(cmsPageStateUpdaters.shareOptionsCloseUpdater),
                    onCopy: () => setState(cmsPageStateUpdaters.onCopyUpdater),
                    onShare: DataLayer.shareDossier,
                  }}
                />
              </GenericPageHeaderWrapper>
              
              {highlightBlock}
              {newsBlock}
              {agendaBlock}
            </>
          )
        }),
        blocksWidget(acceptVideoCookie)(props.blocks).map((updater) => cmsPageStateUpdaters.blocksUpdater(updater)),
      ])
